/**
 * 계정 프로필 WebP: 최초 fetch 후 IndexedDB + 메모리 Blob URL로 재사용.
 * `path|version`이 같으면 네트워크를 다시 타지 않는다.
 */

import { userProfileImageCacheVersion } from './userProfileImage';

const DB_NAME = 'ClassPlannerUserProfileImageCache';
const DB_VERSION = 1;
const STORE = 'images';
const CACHE_SCHEMA = 'v1';

type CacheRecord = {
  buffer: ArrayBuffer;
  version: string;
  mime: string;
};

type MemoryEntry = {
  blobUrl: string;
  version: string;
};

const memoryByUserId = new Map<string, MemoryEntry>();
const inflightByKey = new Map<string, Promise<string | null>>();

export type UserProfileImageCacheRef = {
  userId: string;
  path: string;
  url: string;
  version: number;
};

function cacheKey(userId: string): string {
  return `${CACHE_SCHEMA}:${userId.trim()}`;
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<CacheRecord | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<CacheRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as CacheRecord | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, record: CacheRecord): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(record, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function idbDelete(key: string): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).delete(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function fetchImageArrayBuffer(
  sourceUrl: string
): Promise<{ buffer: ArrayBuffer; mime: string } | null> {
  try {
    const res = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'force-cache',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength === 0) return null;
    const mime = res.headers.get('content-type')?.split(';')[0]?.trim() || 'image/webp';
    return { buffer, mime };
  } catch {
    return null;
  }
}

function remember(userId: string, version: string, buffer: ArrayBuffer, mime: string): string {
  const prev = memoryByUserId.get(userId);
  if (prev && prev.version !== version) {
    try {
      URL.revokeObjectURL(prev.blobUrl);
    } catch {
      /* ignore */
    }
  }
  const blobUrl = URL.createObjectURL(new Blob([buffer], { type: mime || 'image/webp' }));
  memoryByUserId.set(userId, { blobUrl, version });
  return blobUrl;
}

export function peekCachedUserProfileImageUrl(image: Pick<UserProfileImageCacheRef, 'userId' | 'path' | 'version'>): string | null {
  const userId = image.userId.trim();
  if (!userId) return null;
  const version = userProfileImageCacheVersion(image.path, image.version);
  const mem = memoryByUserId.get(userId);
  if (mem && mem.version === version) return mem.blobUrl;
  return null;
}

export async function getCachedUserProfileImageUrl(
  image: UserProfileImageCacheRef,
  options?: { allowNetwork?: boolean }
): Promise<string | null> {
  const userId = image.userId.trim();
  const sourceUrl = image.url.trim();
  const allowNetwork = options?.allowNetwork !== false;
  if (!userId) return null;
  if (allowNetwork && !sourceUrl) return null;
  const version = userProfileImageCacheVersion(image.path, image.version);
  const idbKey = cacheKey(userId);

  const mem = memoryByUserId.get(userId);
  if (mem && mem.version === version) return mem.blobUrl;

  const inflightKey = `${idbKey}:${allowNetwork ? 'net' : 'cache'}`;
  let inflight = inflightByKey.get(inflightKey);
  if (!inflight) {
    inflight = (async (): Promise<string | null> => {
      try {
        const cached = await idbGet(idbKey);
        if (cached && cached.buffer?.byteLength > 0 && cached.version === version) {
          return remember(userId, version, cached.buffer, cached.mime || 'image/webp');
        }
        if (!allowNetwork && cached?.buffer?.byteLength) {
          return remember(userId, cached.version, cached.buffer, cached.mime || 'image/webp');
        }
      } catch {
        /* IDB 미지원 */
      }

      if (!allowNetwork) return null;

      const got = await fetchImageArrayBuffer(sourceUrl);
      if (!got) {
        try {
          const stale = await idbGet(idbKey);
          if (stale?.buffer?.byteLength) {
            return remember(userId, stale.version, stale.buffer, stale.mime || 'image/webp');
          }
        } catch {
          /* ignore */
        }
        return null;
      }

      try {
        await idbPut(idbKey, { buffer: got.buffer, version, mime: got.mime });
      } catch {
        /* 저장 실패해도 표시는 가능 */
      }
      return remember(userId, version, got.buffer, got.mime);
    })().finally(() => {
      inflightByKey.delete(inflightKey);
    });
    inflightByKey.set(inflightKey, inflight);
  }
  return inflight;
}

export async function putUserProfileImageCacheFromBlob(
  image: Pick<UserProfileImageCacheRef, 'userId' | 'path' | 'version'>,
  blob: Blob
): Promise<string | null> {
  const userId = image.userId.trim();
  if (!userId || blob.size <= 0) return null;
  const version = userProfileImageCacheVersion(image.path, image.version);
  const mime = blob.type || 'image/webp';
  const buffer = await blob.arrayBuffer();
  try {
    await idbPut(cacheKey(userId), { buffer, version, mime });
  } catch {
    /* ignore */
  }
  return remember(userId, version, buffer, mime);
}

export async function invalidateUserProfileImageCache(userId: string): Promise<void> {
  const id = userId.trim();
  if (!id) return;
  const mem = memoryByUserId.get(id);
  if (mem) {
    try {
      URL.revokeObjectURL(mem.blobUrl);
    } catch {
      /* ignore */
    }
    memoryByUserId.delete(id);
  }
  inflightByKey.delete(`${cacheKey(id)}:net`);
  inflightByKey.delete(`${cacheKey(id)}:cache`);
  try {
    await idbDelete(cacheKey(id));
  } catch {
    /* ignore */
  }
}

export function clearUserProfileImageMemoryCaches(): void {
  Array.from(memoryByUserId.values()).forEach((entry) => {
    try {
      URL.revokeObjectURL(entry.blobUrl);
    } catch {
      /* ignore */
    }
  });
  memoryByUserId.clear();
  inflightByKey.clear();
}
